import React from 'react'
import { BarChart3, MapPin, Cloud } from 'lucide-react'
import { LayerConfig } from '../../types/map'

interface SidebarProps {
  coffeeShopCount: number
  isLoading: boolean
  layers: LayerConfig[]
  setLayers: React.Dispatch<React.SetStateAction<LayerConfig[]>>
}

// Color mapping object to ensure Tailwind classes are statically recognized
const colorClassMap: Record<string, string> = {
  'coffee': 'bg-coffee-500',
  'blue': 'bg-blue-500',
  'red': 'bg-red-500',
  'green': 'bg-green-500',
  'yellow': 'bg-yellow-500',
  'purple': 'bg-purple-500',
  'pink': 'bg-pink-500',
  'indigo': 'bg-indigo-500',
  'gray': 'bg-gray-500',
  'slate': 'bg-slate-500',
  'zinc': 'bg-zinc-500',
  'neutral': 'bg-neutral-500',
  'stone': 'bg-stone-500',
  'orange': 'bg-orange-500',
  'amber': 'bg-amber-500',
  'lime': 'bg-lime-500',
  'emerald': 'bg-emerald-500',
  'teal': 'bg-teal-500',
  'cyan': 'bg-cyan-500',
  'sky': 'bg-sky-500',
  'violet': 'bg-violet-500',
  'fuchsia': 'bg-fuchsia-500',
  'rose': 'bg-rose-500'
}

const Sidebar: React.FC<SidebarProps> = ({
  coffeeShopCount,
  isLoading,
  layers,
  setLayers
}) => {
  // Handler for layer checkbox changes
  const handleLayerChange = (layerName: string) => {
    setLayers(prevLayers =>
      prevLayers.map(layer =>
        layer.name === layerName
          ? { ...layer, enabled: !layer.enabled }
          : layer
      )
    )
  }

  return (
    <aside className="sidebar">
      {/* Statistics Section */}
      <div className="p-4 lg:p-6 border-b border-neutral-200">
        <div className="flex items-center space-x-2 mb-4">
          <BarChart3 className="w-5 h-5 text-neutral-600" />
          <h2 className="text-lg font-semibold text-neutral-900">Statistics</h2>
        </div>
        <div className="space-y-4">
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <MapPin className="w-4 h-4 text-coffee-600" />
              <span className="text-sm font-medium text-neutral-700">Coffee Shops</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900">
              {isLoading ? '...' : coffeeShopCount}
            </p>
          </div>
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Cloud className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-neutral-700">Weather</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900">--°C</p>
          </div>
        </div>
      </div>
      {/* Layer Controls */}
      <div className="p-4 lg:p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Layers</h2>
        <div className="space-y-3">
          {layers.map((layer) => (
            <label key={layer.name} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-neutral-50 transition-colors">
              <input
                type="checkbox"
                checked={layer.enabled}
                onChange={() => handleLayerChange(layer.name)}
                className="rounded border-neutral-300 text-coffee-600 focus:ring-coffee-500 cursor-pointer"
              />
              <div className={`w-3 h-3 rounded-full ${colorClassMap[layer.color] || 'bg-gray-500'}`}></div>
              <span className="text-sm text-neutral-700">{layer.name}</span>
            </label>
          ))}
        </div>
      </div>
    </aside>
  )
}

export default Sidebar 